import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  Button,
  Tooltip,
  CircularProgress,
  Paper,
  Alert,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  assignEmployees,
  getCandidatesByArea,
  checkCandidateConflict,
} from "../../../api/services/scheduling";
import EmployeeHistoryModal from "../../../screens/ClientsScreen/EmployeeHistoryModal";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import ConflictModal from "../../../screens/ClientsScreen/ConflictModal";
import { QK } from "../../../base/qk";
import CustomInput from "../../FormFields/CustomInput";

const AddScheduleEmployee = ({
  area,
  jobId,
  assignmentId,
  show,
  handleClose,
  jobDetails,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [historyModalEmployee, setHistoryModalEmployee] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictsData, setConflictsData] = useState([]);

  const { data: employees, isLoading } = useQuery(
    [QK.employees, area, search, assignmentId],
    () => getCandidatesByArea(area, search, assignmentId, true),
    {
      enabled: !!area || !!search || !!assignmentId,
    }
  );

  const queryClient = useQueryClient();

  const handleCheckboxChange = (id) => {
    setSelectedEmployees((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((empId) => empId !== id)
        : [...prevSelected, id]
    );
    setErrorMessage("");
  };

  const handleShowHistory = (id) => {
    setHistoryModalEmployee(id);
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setHistoryModalEmployee(null);
  };

  const handleAssignEmployee = async () => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const response = await assignEmployees({
        schedule_job_id: jobId,
        employee_ids: selectedEmployees,
      });
      if (response.status === 200) {
        enqueueSnackbar(response.data?.message || "Employees allocated successfully", {
          variant: "success",
        });
        setSelectedEmployees([]);
        queryClient.invalidateQueries(["job-details", jobId]);
        queryClient.invalidateQueries([QK.assignmentDetails]);
        queryClient.invalidateQueries([QK.scheduleTimeline]);
        setShowConfirmModal(false);
        handleClose();
      }
    } catch (error) {
      let message = "Failed to allocate employees";
      if (error?.response?.data?.error) {
        message = error.response.data.error;
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (selectedEmployees.length === 0) {
      setErrorMessage("Please select at least one employee");
      return;
    }

    const startTime = jobDetails?.scheduled_start
      ? jobDetails.scheduled_start.split('.')[0]
      : jobDetails?.time_required_from;

    const endTime = jobDetails?.scheduled_end
      ? jobDetails.scheduled_end.split('.')[0]
      : jobDetails?.time_required_to;

    const data = {
      employee_list: selectedEmployees,
      start_time: startTime,
      end_time: endTime,
      day: jobDetails?.weekly_day,
    };

    try {
      const conflicts = await checkCandidateConflict(data);
      if (conflicts && conflicts?.length > 0) {
        setConflictsData(conflicts);
        setShowConflictModal(true);
      } else {
        setShowConfirmModal(true);
      }
    } catch (error) {
      let message = "Failed to check conflicts";
      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error?.message) {
        message = error.message;
      }
      setErrorMessage(message);
    }
  };

  const handleConfirm = () => {
    handleAssignEmployee();
  };

  const handleModalClose = () => {
    setSearch("");
    setSelectedEmployees([]);
    setErrorMessage("");
    setShowConfirmModal(false);
    setShowConflictModal(false);
    setConflictsData([]);
    handleClose();
  };

  return (
    <>
      <Dialog
        open={show}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2.5,
              pb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                  👥
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                  Select Employees
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  Assign employees to this schedule
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleModalClose}
              size="small"
              sx={{
                color: "#9CA3AF",
                "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ opacity: 0.4 }} />
        </DialogTitle>

        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              {errorMessage && (
                <Alert
                  severity="error"
                  onClose={() => setErrorMessage("")}
                  sx={{
                    borderRadius: "8px",
                    mb: 1,
                  }}
                >
                  {errorMessage}
                </Alert>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SearchIcon sx={{ color: "#94A3B8" }} />
                <CustomInput
                  placeholder="Search employees by name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" fontWeight={600} color="#475467">
                  {employees?.length || 0} employees found
                </Typography>
                <Chip
                  label={`${selectedEmployees.length} selected`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    bgcolor: selectedEmployees.length > 0
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "#F1F5F9",
                    color: selectedEmployees.length > 0
                      ? theme.palette.primary.main
                      : "#475467",
                  }}
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  height: "380px",
                  overflowY: "auto",
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  p: 1,
                  "&::-webkit-scrollbar": {
                    width: 6,
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "#F1F1F1",
                    borderRadius: 3,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#C1C1C1",
                    borderRadius: 3,
                    "&:hover": {
                      bgcolor: "#A8A8A8",
                    },
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : employees?.length > 0 ? (
                  employees.map((employee) => {
                    const isChecked = selectedEmployees.includes(employee.id);

                    return (
                      <Paper
                        key={employee.id}
                        elevation={0}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1.5,
                          mb: 1,
                          borderRadius: "8px",
                          border: "1px solid #E2E8F0",
                          bgcolor: isChecked
                            ? alpha("#2469F0", 0.04)
                            : employee.is_overlap
                            ? "#FEF2F2"
                            : "#FFFFFF",
                          opacity: employee.is_overlap ? 0.6 : 1,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: isChecked
                              ? "#2469F0"
                              : employee.is_overlap
                              ? "#FCA5A5"
                              : "#E2E8F0",
                            boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Avatar
                              src={employee.profile_photo || ""}
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                fontSize: "1rem",
                                fontWeight: 600,
                              }}
                            >
                              {!employee.profile_photo &&
                                `${employee.first_name?.[0]}${employee.last_name?.[0]}`}
                            </Avatar>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={() =>
                                    handleCheckboxChange(employee.id)
                                  }
                                  disabled={employee.is_overlap}
                                  sx={{
                                    color: "#94A3B8",
                                    "&.Mui-checked": {
                                      color: "#2469F0",
                                    },
                                    padding: "4px",
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color="#1A1A2E"
                                  >
                                    {employee.first_name} {employee.last_name}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                    sx={{ mt: 0.25 }}
                                  >
                                    <LocationOnIcon
                                      sx={{ fontSize: 11, color: "#94A3B8" }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#64748B",
                                        fontSize: "0.6rem",
                                      }}
                                    >
                                      {employee.location_name || "N/A"} •{" "}
                                      {employee.area_name || "N/A"}
                                    </Typography>
                                  </Stack>
                                </Box>
                              }
                              sx={{ mr: 0, alignItems: "center" }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            {employee.is_my_zones && (
                              <Chip
                                label="Patch"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.5rem",
                                  fontWeight: 600,
                                  bgcolor: alpha("#3B82F6", 0.08),
                                  color: "#3B82F6",
                                }}
                              />
                            )}
                            {employee.is_all_zones && (
                              <Chip
                                label="All Areas"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.5rem",
                                  fontWeight: 600,
                                  bgcolor: alpha("#8B5CF6", 0.08),
                                  color: "#8B5CF6",
                                }}
                              />
                            )}
                            {employee.base_location && (
                              <Chip
                                label={`Base: ${employee.base_location}`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.5rem",
                                  fontWeight: 500,
                                  bgcolor: alpha("#F59E0B", 0.08),
                                  color: "#D97706",
                                }}
                              />
                            )}
                            {employee.skills?.slice(0, 2).map((skill) => (
                              <Chip
                                key={skill.id}
                                label={skill.name}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.5rem",
                                  fontWeight: 500,
                                  bgcolor: "#F1F5F9",
                                  color: "#475467",
                                }}
                              />
                            ))}
                            {employee.skills?.length > 2 && (
                              <Chip
                                label={`+${employee.skills.length - 2}`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.5rem",
                                  fontWeight: 600,
                                  bgcolor: "#F1F5F9",
                                  color: "#475467",
                                }}
                              />
                            )}
                            {employee.status === "active" && (
                              <Tooltip title="Active" arrow>
                                <CheckCircleIcon
                                  sx={{ fontSize: 14, color: "#22C55E" }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="View Weekly History" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleShowHistory(employee.id)}
                              sx={{
                                color: "#94A3B8",
                                "&:hover": {
                                  color: "#2469F0",
                                  bgcolor: alpha("#2469F0", 0.06),
                                },
                              }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {employee.is_overlap && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ fontSize: "0.55rem" }}
                            >
                              ⚠️ Conflict
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      No employees found for <strong>"{search}"</strong>
                    </Typography>
                  </Box>
                )}
              </Paper>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Button
                  onClick={handleModalClose}
                  variant="outlined"
                  disabled={isSubmitting}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "10px",
                    borderColor: "#D0D5DD",
                    color: "#344054",
                    px: 3,
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                      borderColor: "#98A2B3",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || selectedEmployees.length === 0}
                  startIcon={
                    isSubmitting ? <CircularProgress size={20} /> : null
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    bgcolor: "#2469F0",
                    px: 4,
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                    },
                  }}
                >
                  {isSubmitting
                    ? "Allocating..."
                    : `Allocate (${selectedEmployees.length})`}
                </Button>
              </Box>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        isLoading={isSubmitting}
        title="Assign Employees"
        body={`Are you sure you want to assign ${selectedEmployees.length} employee(s) to this schedule job?`}
        confirmText="Yes, Assign"
        cancelText="Cancel"
      />

      {showConflictModal && (
        <ConflictModal
          open={showConflictModal}
          onClose={() => {
            setShowConflictModal(false);
            setConflictsData([]);
          }}
          conflictData={conflictsData}
          onAssignAnyway={handleConfirm}
        />
      )}

      {historyModalEmployee && (
        <EmployeeHistoryModal
          show={showHistoryModal}
          handleClose={handleCloseHistoryModal}
          employeeId={historyModalEmployee}
        />
      )}
    </>
  );
};

export default AddScheduleEmployee;